import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { format } from 'date-fns';
import {
  ArrowLeft, AlertTriangle, Clock, User, Tag, Layers,
  Trash2, UserCheck, TrendingUp, CheckCircle2,
} from 'lucide-react';
import { PERMISSIONS } from '@pms/shared';
import { techSupportService } from '@/services/tech-support.service';
import { usePermission } from '@/hooks/usePermission';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { TicketStatusBadge } from './components/TicketStatusBadge';
import { TicketPriorityBadge } from './components/TicketPriorityBadge';
import { TicketCommentSection } from './components/TicketCommentSection';
import { TicketActivityTimeline } from './components/TicketActivityTimeline';
import { TicketAssignModal } from './components/TicketAssignModal';
import { TicketEscalateModal } from './components/TicketEscalateModal';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';

const STATUS_TRANSITIONS: Record<string, { value: string; label: string }[]> = {
  open:        [{ value: 'in_progress', label: 'Start Progress' }, { value: 'waiting', label: 'Mark Waiting' }, { value: 'closed', label: 'Close' }],
  in_progress: [{ value: 'waiting', label: 'Mark Waiting' }, { value: 'resolved', label: 'Mark Resolved' }, { value: 'open', label: 'Reopen' }],
  waiting:     [{ value: 'in_progress', label: 'Resume' }, { value: 'resolved', label: 'Mark Resolved' }],
  resolved:    [{ value: 'in_progress', label: 'Reopen' }, { value: 'closed', label: 'Close' }],
  closed:      [],
};

export function TechSupportDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();

  const [assignOpen, setAssignOpen] = useState(false);
  const [escalateOpen, setEscalateOpen] = useState(false);
  const [deleteTicketConfirm, setDeleteTicketConfirm] = useState(false);

  const { has } = usePermission();
  const canUpdate = has(PERMISSIONS.TECH_SUPPORT_UPDATE);
  const canAssign = has(PERMISSIONS.TECH_SUPPORT_ASSIGN);
  const canEscalate = has(PERMISSIONS.TECH_SUPPORT_ESCALATE);
  const canClose = has(PERMISSIONS.TECH_SUPPORT_CLOSE);
  const canDelete = has(PERMISSIONS.TECH_SUPPORT_DELETE);

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['tech-support', 'tickets', id],
    queryFn: () => techSupportService.getTicket(id!),
    enabled: !!id,
  });

  const ticket = data?.data?.data;

  const statusMutation = useMutation({
    mutationFn: (status: string) =>
      techSupportService.changeStatus(id!, { status: status as never }),
    onSuccess: () => {
      toast.success('Status updated');
      qc.invalidateQueries({ queryKey: ['tech-support', 'tickets', id] });
      qc.invalidateQueries({ queryKey: ['tech-support', 'tickets'] });
    },
    onError: (err: Error) => toast.error(err.message ?? 'Failed to update status'),
  });

  const deleteMutation = useMutation({
    mutationFn: () => techSupportService.deleteTicket(id!),
    onSuccess: () => {
      toast.success('Ticket deleted');
      navigate('/tech-support');
    },
    onError: () => toast.error('Failed to delete ticket'),
  });

  if (isLoading) {
    return (
      <div className="space-y-5 animate-pulse">
        <div className="h-6 w-32 bg-muted rounded" />
        <div className="grid lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-4">
            <div className="h-40 bg-muted rounded-xl" />
            <div className="h-60 bg-muted rounded-xl" />
          </div>
          <div className="h-80 bg-muted rounded-xl" />
        </div>
      </div>
    );
  }

  if (isError || !ticket) {
    return (
      <div className="space-y-5">
        <Button variant="ghost" size="sm" onClick={() => navigate('/tech-support')}>
          <ArrowLeft className="w-4 h-4 mr-1" /> Back
        </Button>
        <div className="flex flex-col items-center justify-center py-16 gap-3 text-muted-foreground">
          <p>Failed to load ticket.</p>
          <Button variant="outline" size="sm" onClick={() => refetch()}>Retry</Button>
        </div>
      </div>
    );
  }

  const transitions = STATUS_TRANSITIONS[ticket.status] ?? [];
  const visibleTransitions = transitions.filter((t) => {
    if (t.value === 'closed') return canClose;
    return canUpdate;
  });

  return (
    <div className="space-y-5">
      <Button variant="ghost" size="sm" onClick={() => navigate('/tech-support')} className="text-muted-foreground hover:text-foreground -ml-1">
        <ArrowLeft className="w-4 h-4 mr-1.5" />
        Back to tickets
      </Button>

      {/* Title row */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-mono text-sm text-primary font-semibold">{ticket.ticket_number}</span>
            <TicketStatusBadge status={ticket.status} />
            <TicketPriorityBadge priority={ticket.priority} />
            {ticket.is_overdue && (
              <span className="flex items-center gap-1 text-xs text-destructive font-medium">
                <AlertTriangle className="w-3.5 h-3.5" /> Overdue
              </span>
            )}
          </div>
          <h1 className="text-xl sm:text-2xl font-semibold tracking-tight mt-1">{ticket.title}</h1>
        </div>

        <div className="flex flex-wrap gap-2 flex-shrink-0">
          {visibleTransitions.map((t) => (
            <Button
              key={t.value}
              variant="outline"
              size="sm"
              onClick={() => statusMutation.mutate(t.value)}
              disabled={statusMutation.isPending}
            >
              {t.label}
            </Button>
          ))}
          {canAssign && (
            <Button variant="outline" size="sm" onClick={() => setAssignOpen(true)}>
              <UserCheck className="w-3.5 h-3.5 mr-1" /> Assign
            </Button>
          )}
          {canEscalate && ticket.escalation_level < 3 && (
            <Button variant="outline" size="sm" onClick={() => setEscalateOpen(true)}>
              <TrendingUp className="w-3.5 h-3.5 mr-1" /> Escalate
            </Button>
          )}
          {canDelete && (
            <Button
              variant="destructive"
              size="sm"
              disabled={deleteMutation.isPending}
              onClick={() => setDeleteTicketConfirm(true)}
            >
              <Trash2 className="w-3.5 h-3.5 mr-1" /> Delete
            </Button>
          )}
        </div>
      </div>

      {/* Body */}
      <div className="grid lg:grid-cols-3 gap-6">
        {/* Left — description + comments + timeline */}
        <div className="lg:col-span-2 space-y-5">
          {ticket.description && (
            <Card>
              <CardContent className="p-4 sm:p-5">
                <h2 className="text-sm font-semibold text-foreground mb-2">Description</h2>
                <p className="text-sm text-muted-foreground whitespace-pre-wrap leading-relaxed">{ticket.description}</p>
              </CardContent>
            </Card>
          )}

          <Card>
            <CardContent className="p-4 sm:p-5">
              <TicketCommentSection ticketId={ticket.id} comments={ticket.comments} />
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4 sm:p-5">
              <TicketActivityTimeline auditLog={ticket.audit_log} />
            </CardContent>
          </Card>
        </div>

        {/* Right — metadata */}
        <div>
          <Card>
            <CardContent className="p-4 sm:p-5 space-y-4">
              <h2 className="text-sm font-semibold text-foreground">Details</h2>

              <MetaRow icon={Tag} label="Category" value={ticket.category?.name ?? '—'} />
              <MetaRow icon={User} label="Created by" value={ticket.created_by.name} />
              <MetaRow
                icon={UserCheck}
                label="Assigned to"
                value={ticket.assigned_to?.name ?? <span className="italic text-muted-foreground">Unassigned</span>}
              />
              <MetaRow
                icon={Layers}
                label="Escalation level"
                value={ticket.escalation_level === 0 ? 'None' : `Level ${ticket.escalation_level}`}
              />

              {ticket.sla_due_at && (
                <div className="pt-1 border-t border-border/60">
                  <p className="text-xs text-muted-foreground mb-1 flex items-center gap-1">
                    <Clock className="w-3.5 h-3.5" /> SLA Due
                  </p>
                  <p className={`text-sm font-medium ${ticket.is_overdue ? 'text-destructive' : 'text-foreground'}`}>
                    {ticket.is_overdue && <AlertTriangle className="inline w-3.5 h-3.5 mr-1" />}
                    {format(new Date(ticket.sla_due_at), 'MMM d, yyyy h:mm a')}
                  </p>
                </div>
              )}

              {ticket.resolved_at && (
                <MetaRow
                  icon={CheckCircle2}
                  label="Resolved at"
                  value={format(new Date(ticket.resolved_at), 'MMM d, yyyy h:mm a')}
                />
              )}

              <div className="pt-1 border-t border-border/60 text-xs text-muted-foreground space-y-1">
                <p>Created {format(new Date(ticket.created_at), 'MMM d, yyyy')}</p>
                <p>Updated {format(new Date(ticket.updated_at), 'MMM d, yyyy')}</p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {assignOpen && (
        <TicketAssignModal
          ticketId={ticket.id}
          currentAssignee={ticket.assigned_to}
          onClose={() => setAssignOpen(false)}
        />
      )}
      {escalateOpen && (
        <TicketEscalateModal
          ticketId={ticket.id}
          currentLevel={ticket.escalation_level}
          onClose={() => setEscalateOpen(false)}
        />
      )}

      <ConfirmDialog
        open={deleteTicketConfirm}
        onOpenChange={setDeleteTicketConfirm}
        title="Delete this ticket?"
        description="This cannot be undone."
        confirmLabel="Delete"
        onConfirm={() => deleteMutation.mutate()}
      />
    </div>
  );
}

function MetaRow({
  icon: Icon,
  label,
  value,
}: {
  icon: React.ElementType;
  label: string;
  value: React.ReactNode;
}) {
  return (
    <div className="flex items-start gap-2">
      <Icon className="w-3.5 h-3.5 text-muted-foreground mt-0.5 flex-shrink-0" />
      <div className="min-w-0">
        <p className="text-xs text-muted-foreground">{label}</p>
        <p className="text-sm text-foreground">{value}</p>
      </div>
    </div>
  );
}
